import React, { useMemo } from 'react';
import { useMarketCategories } from '../../contexts/MarketCategoriesContext';
import { useBenefitsPerTier } from '../../contexts/BenefitsPerTierContext';
import { useFiscalEndReviewPageStyles } from '../../styles/FiscalEndReviewPage.styles';
import { LastUpdatedLabel } from '../../components/LastUpdatedLabel';
import { pickMostRecent } from '../../utils/lastUpdated';

/**
 * Tabs that have their own "Last updated" timestamp. Each maps to a different
 * resource type in Cosmos (MarketCategories / BenefitsPerTier /
 * MarketThresholds), so the surfaced timestamp is independent per tab.
 */
export type LastUpdatedTab =
  | 'market-categories'
  | 'benefits-per-tier'
  | 'thresholds-by-market';

interface ProgramChangesLastUpdatedHeaderProps {
  selectedTab: LastUpdatedTab;
}

/**
 * Renders the page-level "Last updated: {when}" pill that lives above the
 * TabList. The displayed timestamp tracks whichever tab is currently
 * selected — Market Categories, Benefits per Tier, and Thresholds by Market
 * each have their own audit history in Cosmos and update on independent
 * cadences (each can be edited / approved separately during a cycle).
 *
 * The label collapses an entire tab's per-row history down to the single
 * most-recent applied change. Reviewers wanted a quick "is this section
 * fresh?" signal; per-row stamps cluttered the table without adding
 * actionable info.
 */
export const ProgramChangesLastUpdatedHeader: React.FC<
  ProgramChangesLastUpdatedHeaderProps
> = ({ selectedTab }) => {
  const styles = useFiscalEndReviewPageStyles();
  const { categories, loading: categoriesLoading } = useMarketCategories();
  const { benefits, loading: benefitsLoading } = useBenefitsPerTier();

  const display = useMemo(() => {
    switch (selectedTab) {
      case 'market-categories':
        return {
          info: pickMostRecent(categories.map((c) => c.lastUpdatedCategory)),
          loading: categoriesLoading,
        };
      case 'thresholds-by-market':
        return {
          info: pickMostRecent(categories.map((c) => c.lastUpdatedThresholds)),
          loading: categoriesLoading,
        };
      case 'benefits-per-tier':
        return {
          info: pickMostRecent(benefits.map((b) => b.lastUpdated)),
          loading: benefitsLoading,
        };
      default:
        return { info: undefined, loading: false };
    }
  }, [selectedTab, categories, benefits, categoriesLoading, benefitsLoading]);

  return (
    <div className={styles.ttmInfoRow}>
      <LastUpdatedLabel at={display.info?.at} loading={display.loading} />
    </div>
  );
};
